// src/pages/settings/GeneralSettingsPage.jsx

import React, {

  useEffect,

  useMemo,

  useState,

} from "react";

import {

  Alert,

  Button,

  Card,

  Col,

  Form,

  Nav,

  Row,

  Spinner,

} from "react-bootstrap";

import {

  useDispatch,

  useSelector,

} from "react-redux";

import { toast } from "react-toastify";

import { getPerm } from "../../../utils/perm";

import {

  clearGeneralMsg,

  fetchGeneralSettingsThunk,

  saveGeneralSettingsThunk,

} from "../../../store/slices/generalSettingsSlice";

import {

  fetchHouseApprovalSettingsThunk,

  fetchApprovalUsersThunk,

  saveHouseApprovalSettingsThunk,

  clearHouseApprovalSettingsError,

  clearHouseApprovalSaveStatus,

  resetHouseApprovalSettings,

} from "../../../store/slices/houseApprovalSettingsSlice";

import "../../../css/master-common.css";

// ============================================================

// CONSTANTS

// ============================================================

const QR_BASE =

  "https://pragathiapp.in/uploads/qr";

// ============================================================

// HELPERS

// ============================================================

const normalizeQrSrc = (value) => {

  if (!value) return "";

  const source =

    String(value).trim();

  if (

    source.startsWith("blob:") ||

    source.startsWith("http://") ||

    source.startsWith("https://")

  ) {

    return source;

  }

  return `${QR_BASE}/${source}`;

};

const normalizeYesNo = (value) => {

  const normalized =

    String(value ?? "")

      .trim()

      .toLowerCase();

  return [

    "yes",

    "1",

    "true",

  ].includes(normalized)

    ? "yes"

    : "no";

};

const normalizeReceiptType = (

  value

) => {

  const normalized =

    String(value ?? "")

      .trim()

      .toLowerCase();

  return normalized ===

    "together"

    ? "together"

    : "separate";

};

const normalizeApprovalFlag = (

  value

) => {

  const normalized =

    String(value ?? "")

      .trim()

      .toLowerCase();

  return [

    "1",

    "yes",

    "true",

  ].includes(normalized)

    ? "yes"

    : "no";

};

// ============================================================

// SECTION TITLE

// ============================================================

const SectionTitle = ({

  title,

  description,

}) => (

  <div className="gs-section-head">

    <div className="gs-section-title">

      {title}

    </div>

    {description ? (

      <div className="gs-section-subtitle">

        {description}

      </div>

    ) : null}

  </div>

);

// ============================================================

// QR CONTROL

// ============================================================

const QrUpload = ({

  label,

  src,

  disabled,

  onPick,

  onRemove,

}) => {

  return (

    <Form.Group>

      <Form.Label className="gs-label">

        {label}

      </Form.Label>

      <div className="gs-qr-layout">

        <div className="gs-qr-input">

          <Form.Control

            className="gs-control"

            type="file"

            accept="image/*"

            disabled={disabled}

            onChange={(event) =>

              onPick(

                event.target.files?.[0]

              )

            }

          />

          <div className="gs-help">

            Upload QR image. Maximum

            size 2 MB.

          </div>

          {src ? (

            <Button

              type="button"

              variant="outline-danger"

              size="sm"

              disabled={disabled}

              className="gs-remove-btn mt-2"

              onClick={onRemove}

            >

              Remove

            </Button>

          ) : null}

        </div>

        <div className="gs-qr-preview">

          {src ? (

            <img

              src={src}

              alt={label}

              style={{ objectFit: "contain" }}

            />

          ) : (

            <span className="gs-qr-empty">

              No QR

            </span>

          )}

        </div>

      </div>

    </Form.Group>

  );

};

// ============================================================

// APPROVAL ROW

// ============================================================

const ApprovalRow = ({

  title,

  description,

  approvalValue,

  userValue,

  users,

  usersLoading,

  disabled,

  onApprovalChange,

  onUserChange,

}) => {

  const approvalEnabled =

    approvalValue === "yes";

  return (

    <Row className="g-3 align-items-end gs-approval-row">

      <Col

        xs={12}

        md={4}

      >

        <div className="gs-approval-name">

          {title}

        </div>

        <div className="gs-approval-description">

          {description}

        </div>

      </Col>

      <Col

        xs={12}

        md={3}

      >

        <Form.Label

          className="d-md-none"

          style={{

            fontWeight: 500,

          }}

        >

          Approval Required

        </Form.Label>

        <Form.Select

          className="gs-control"

          value={approvalValue}

          disabled={disabled}

          onChange={(event) =>

            onApprovalChange(

              event.target.value

            )

          }

        >

          <option value="yes">

            Yes

          </option>

          <option value="no">

            No

          </option>

        </Form.Select>

      </Col>

      <Col

        xs={12}

        md={5}

      >

        <Form.Label

          className="d-md-none"

          style={{

            fontWeight: 500,

          }}

        >

          Approval User

        </Form.Label>

        <Form.Select

          className="gs-control"

          value={userValue}

          disabled={

            disabled ||

            usersLoading ||

            !approvalEnabled

          }

          onChange={(event) =>

            onUserChange(

              event.target.value

            )

          }

        >

          <option value="">

            {usersLoading

              ? "Loading users..."

              : approvalEnabled

                ? "Select User"

                : "Approval Not Required"}

          </option>

          {users.map((user) => (

            <option

              key={user.user_id}

              value={user.user_id}

            >

              {user.user_name}

              {" "}

              (#{user.user_id})

            </option>

          ))}

        </Form.Select>

      </Col>

    </Row>

  );

};

// ============================================================

// PAGE

// ============================================================

export default function GeneralSettingsPage() {

  const dispatch =

    useDispatch();

  // ==========================================================

  // ACTIVE PANCHAYATH

  // ==========================================================

  const activePanId =

    useSelector(

      (state) =>

        Number(

          state.tenant

            ?.pan_id_active ||

            0

        )

    );

  // ==========================================================

  // PAGE TAB

  // ==========================================================

  const [

    activeTab,

    setActiveTab,

  ] = useState("general");

  // ==========================================================

  // GENERAL SETTINGS REDUX

  // ==========================================================

  const {

    data,

    loading,

    saving,

    error,

    success,

  } = useSelector(

    (state) =>

      state.generalSettings

  );

  // ==========================================================

  // APPROVAL REDUX

  // ==========================================================

  const {

    settings:

      approvalSettings,

    users:

      approvalUsers,

    loading:

      approvalLoading,

    usersLoading,

    saving:

      approvalSaving,

    error:

      approvalError,

    usersError,

    saveSuccess:

      approvalSaveSuccess,

    saveMessage:

      approvalSaveMessage,

  } = useSelector(

    (state) =>

      state.houseApprovalSettings

  );

  // ==========================================================

  // PERMISSION

  // ==========================================================

  const permsMap =

    useSelector(

      (state) =>

        state.permissions?.map

    ) || {};

  const perms =

    getPerm(

      permsMap,

      "general_settings"

    ) || {};

  const canUpdate =

    Boolean(

      perms.can_update

    );

  // ==========================================================

  // GENERAL FORM

  // ==========================================================

  const [

    form,

    setForm,

  ] = useState({

    title: "",

    slrm_pan_name: "",

    phone: "",

    address: "",

    reading_charge: "",

    slrm_reading_charge: "",
    reading_measure: "",

    unit_conv: "",

    slrm_water_together:

      "no",

    receipt_type:

      "separate",

    water_payment_qr:

      "",

    slrm_payment_qr:

      "",

    water_payment_qr_file:

      null,

    slrm_payment_qr_file:

      null,

    remove_water_payment_qr:

      "0",

    remove_slrm_payment_qr:

      "0",

  });

  // ==========================================================

  // APPROVAL FORM

  // ==========================================================

  const [

    approvalForm,

    setApprovalForm,

  ] = useState({

    disconnection_approval_required:

      "no",

    disconnection_approver_user_id:

      "",

    reconnection_approval_required:

      "no",

    reconnection_approver_user_id:

      "",

    discount_approval_required:

      "no",

    discount_approver_user_id:

      "",

  });

  // ==========================================================

  // GENERAL TOAST

  // ==========================================================

  useEffect(() => {

    if (!success) return;

    toast.success(

      String(success)

    );

    dispatch(

      clearGeneralMsg()

    );

  }, [

    success,

    dispatch,

  ]);

  useEffect(() => {

    if (!error) return;

    toast.error(

      String(error)

    );

    dispatch(

      clearGeneralMsg()

    );

  }, [

    error,

    dispatch,

  ]);

  // ==========================================================

  // APPROVAL TOAST

  // ==========================================================

  useEffect(() => {

    if (

      !approvalSaveSuccess

    ) {

      return;

    }

    toast.success(

      approvalSaveMessage ||

        "Approval settings saved successfully"

    );

    dispatch(

      clearHouseApprovalSaveStatus()

    );

  }, [

    approvalSaveSuccess,

    approvalSaveMessage,

    dispatch,

  ]);

  useEffect(() => {

    if (!approvalError) {

      return;

    }

    toast.error(

      String(

        approvalError

      )

    );

    dispatch(

      clearHouseApprovalSettingsError()

    );

  }, [

    approvalError,

    dispatch,

  ]);

  // ==========================================================

  // LOAD GENERAL SETTINGS

  // ==========================================================

  useEffect(() => {

    dispatch(

      fetchGeneralSettingsThunk()

    );

  }, [

    dispatch,

    activePanId,

  ]);

  // ==========================================================

  // LOAD APPROVAL SETTINGS

  //

  // IMPORTANT:

  // Do NOT call until active Panchayath exists.

  // ==========================================================

  useEffect(() => {

    if (!activePanId) {

      dispatch(

        resetHouseApprovalSettings()

      );

      return;

    }

    dispatch(

      fetchHouseApprovalSettingsThunk(

        activePanId

      )

    );

    dispatch(

      fetchApprovalUsersThunk({

        pan_id: activePanId,

        search: "",

      })

    );

  }, [

    dispatch,

    activePanId,

  ]);

  // ==========================================================

  // GENERAL DATA -> FORM

  // ==========================================================

  useEffect(() => {

    if (!data) return;

    const together =

      normalizeYesNo(

        data.slrm_water_together

      );

    const receiptType =

      together === "yes"

        ? normalizeReceiptType(

            data.receipt_type

          )

        : "separate";

    setForm({

      title:

        data.title ?? "",

      slrm_pan_name:

        data.slrm_pan_name ?? "",

      phone:

        data.phone ?? "",

      address:

        data.address ?? "",

      reading_charge:

        data.reading_charge ??

        "",

      slrm_reading_charge:
        data.slrm_reading_charge ?? "",

      reading_measure:

        data.reading_measure ??

        "",

      unit_conv:

        data.unit_conv ?? "",

      slrm_water_together:

        together,

      receipt_type:

        receiptType,

      water_payment_qr:

        data.water_payment_qr ??

        "",

      slrm_payment_qr:

        data.slrm_payment_qr ??

        "",

      water_payment_qr_file:

        null,

      slrm_payment_qr_file:

        null,

      remove_water_payment_qr:

        "0",

      remove_slrm_payment_qr:

        "0",

    });

  }, [

    data,

  ]);

  // ==========================================================

  // APPROVAL DATA -> FORM

  // ==========================================================

  useEffect(() => {

    if (

      !activePanId ||

      !approvalSettings

    ) {

      return;

    }

    setApprovalForm({

      disconnection_approval_required:

        normalizeApprovalFlag(

          approvalSettings

            .disconnection_approval_required

        ),

      disconnection_approver_user_id:

        approvalSettings

          .disconnection_approver_user_id

          ? String(

              approvalSettings

                .disconnection_approver_user_id

            )

          : "",

      reconnection_approval_required:

        normalizeApprovalFlag(

          approvalSettings

            .reconnection_approval_required

        ),

      reconnection_approver_user_id:

        approvalSettings

          .reconnection_approver_user_id

          ? String(

              approvalSettings

                .reconnection_approver_user_id

            )

          : "",

      discount_approval_required:

        normalizeApprovalFlag(

          approvalSettings

            .discount_approval_required

        ),

      discount_approver_user_id:

        approvalSettings

          .discount_approver_user_id

          ? String(

              approvalSettings

                .discount_approver_user_id

            )

          : "",

    });

  }, [

    activePanId,

    approvalSettings,

  ]);

  // ==========================================================

  // QR PICK

  // ==========================================================

  const onPickQr = (

    fileKey,

    pathKey,

    removeKey,

    file

  ) => {

    if (!file) return;

    if (

      !file.type?.startsWith(

        "image/"

      )

    ) {

      toast.error(

        "Please select an image file"

      );

      return;

    }

    const maxSize =

      2 *

      1024 *

      1024;

    if (

      file.size >

      maxSize

    ) {

      toast.error(

        "Please upload an image up to 2 MB"

      );

      return;

    }

    const previewUrl =

      URL.createObjectURL(

        file

      );

    setForm(

      (previous) => {

        const oldPreview =

          previous[pathKey];

        if (

          oldPreview &&

          String(

            oldPreview

          ).startsWith(

            "blob:"

          )

        ) {

          URL.revokeObjectURL(

            oldPreview

          );

        }

        return {

          ...previous,

          [fileKey]:

            file,

          [pathKey]:

            previewUrl,

          [removeKey]:

            "0",

        };

      }

    );

  };

  // ==========================================================

  // REMOVE QR

  // ==========================================================

  const clearQr = (

    fileKey,

    pathKey,

    removeKey

  ) => {

    setForm(

      (previous) => {

        const oldPreview =

          previous[pathKey];

        if (

          oldPreview &&

          String(

            oldPreview

          ).startsWith(

            "blob:"

          )

        ) {

          URL.revokeObjectURL(

            oldPreview

          );

        }

        return {

          ...previous,

          [fileKey]:

            null,

          [pathKey]:

            "",

          [removeKey]:

            "1",

        };

      }

    );

  };

  // ==========================================================

  // CLEAN BLOB URLS

  // ==========================================================

  useEffect(() => {

    return () => {

      if (

        form.water_payment_qr &&

        String(

          form.water_payment_qr

        ).startsWith(

          "blob:"

        )

      ) {

        URL.revokeObjectURL(

          form.water_payment_qr

        );

      }

      if (

        form.slrm_payment_qr &&

        String(

          form.slrm_payment_qr

        ).startsWith(

          "blob:"

        )

      ) {

        URL.revokeObjectURL(

          form.slrm_payment_qr

        );

      }

    };

  }, [

    form.water_payment_qr,

    form.slrm_payment_qr,

  ]);

  // ==========================================================

  // QR SOURCES

  // ==========================================================

  const waterQrSrc =

    useMemo(

      () =>

        normalizeQrSrc(

          form.water_payment_qr

        ),

      [

        form.water_payment_qr,

      ]

    );

  const slrmQrSrc =

    useMemo(

      () =>

        normalizeQrSrc(

          form.slrm_payment_qr

        ),

      [

        form.slrm_payment_qr,

      ]

    );

  // ==========================================================

  // WATER + SLRM TOGETHER

  // ==========================================================

  const handleSlrmTogetherChange =

    (value) => {

      const normalized =

        normalizeYesNo(

          value

        );

      setForm(

        (previous) => ({

          ...previous,

          slrm_water_together:

            normalized,

          receipt_type:

            normalized === "no"

              ? "separate"

              : normalizeReceiptType(

                  previous.receipt_type

                ),

        })

      );

    };

  // ==========================================================

  // SAVE GENERAL SETTINGS

  // ==========================================================

  const onSubmitGeneral =

    async (event) => {

      event.preventDefault();

      if (!activePanId) {

        toast.error(

          "Please select a Panchayath"

        );

        return;

      }

      if (!canUpdate) {

        toast.error(

          "No Update permission"

        );

        return;

      }

      const together =

        normalizeYesNo(

          form.slrm_water_together

        );

      const receiptType =

        together === "yes"

          ? normalizeReceiptType(

              form.receipt_type

            )

          : "separate";

      const payload = {

        ...form,

        slrm_water_together:

          together,

        receipt_type:

          receiptType,

      };

      const result =

        await dispatch(

          saveGeneralSettingsThunk(

            payload

          )

        );

      if (

        saveGeneralSettingsThunk

          .rejected

          .match(result)

      ) {

        toast.error(

          String(

            result?.payload ||

              result?.error

                ?.message ||

              "Failed to save general settings"

          )

        );

      }

    };

  // ==========================================================

  // CHANGE APPROVAL REQUIRED

  // ==========================================================

  const handleApprovalChange = (

    approvalKey,

    userKey,

    value

  ) => {

    setApprovalForm(

      (previous) => ({

        ...previous,

        [approvalKey]:

          value,

        [userKey]:

          value === "no"

            ? ""

            : previous[userKey],

      })

    );

  };

  // ==========================================================

  // SAVE APPROVAL SETTINGS

  // ==========================================================

  const saveApprovalSettings =

    async () => {

      if (!activePanId) {

        toast.error(

          "Please select a Panchayath"

        );

        return;

      }

      if (!canUpdate) {

        toast.error(

          "No Update permission"

        );

        return;

      }

      if (

        approvalForm

          .disconnection_approval_required ===

          "yes" &&

        !approvalForm

          .disconnection_approver_user_id

      ) {

        toast.error(

          "Select Disconnection approval user"

        );

        return;

      }

      if (

        approvalForm

          .reconnection_approval_required ===

          "yes" &&

        !approvalForm

          .reconnection_approver_user_id

      ) {

        toast.error(

          "Select Reconnection approval user"

        );

        return;

      }

      if (

        approvalForm

          .discount_approval_required ===

          "yes" &&

        !approvalForm

          .discount_approver_user_id

      ) {

        toast.error(

          "Select Discount approval user"

        );

        return;

      }

      const payload = {

        pan_id:

          activePanId,

        disconnection_approval_required:

          approvalForm

            .disconnection_approval_required ===

          "yes"

            ? 1

            : 0,

        disconnection_approver_user_id:

          approvalForm

            .disconnection_approval_required ===

          "yes"

            ? Number(

                approvalForm

                  .disconnection_approver_user_id

              )

            : null,

        reconnection_approval_required:

          approvalForm

            .reconnection_approval_required ===

          "yes"

            ? 1

            : 0,

        reconnection_approver_user_id:

          approvalForm

            .reconnection_approval_required ===

          "yes"

            ? Number(

                approvalForm

                  .reconnection_approver_user_id

              )

            : null,

        discount_approval_required:

          approvalForm

            .discount_approval_required ===

          "yes"

            ? 1

            : 0,

        discount_approver_user_id:

          approvalForm

            .discount_approval_required ===

          "yes"

            ? Number(

                approvalForm

                  .discount_approver_user_id

              )

            : null,

      };

      const result =

        await dispatch(

          saveHouseApprovalSettingsThunk(

            payload

          )

        );

      if (

        saveHouseApprovalSettingsThunk

          .rejected

          .match(result)

      ) {

        toast.error(

          String(

            result?.payload ||

              result?.error

                ?.message ||

              "Failed to save approval settings"

          )

        );

      }

    };

  // ==========================================================

  // RENDER

  // ==========================================================

  return (

    <div className="gs-page">

      {/* =====================================================

          PAGE HEADER

      ===================================================== */}

      <div className="gs-header">

        <h4 className="gs-title">

          General Settings

        </h4>

        <div className="gs-subtitle">

          Manage Panchayath, billing and approval settings.

        </div>

      </div>

      {/* =====================================================

          TAB CARD

      ===================================================== */}

      <Card className="gs-tabs-card">

        {/* ===================================================

            TABS

        =================================================== */}

        <Card.Header className="gs-tabs-header">

          <Nav

            variant="tabs"

            className="gs-tabs"

            activeKey={activeTab}

            onSelect={(key) =>

              setActiveTab(key || "general")

            }

          >

            <Nav.Item>

              <Nav.Link eventKey="general">

                General Settings

              </Nav.Link>

            </Nav.Item>

            <Nav.Item>

              <Nav.Link eventKey="approval">

                Approval Settings

              </Nav.Link>

            </Nav.Item>

          </Nav>

        </Card.Header>

        <Card.Body className="gs-tab-body">

          {/* =================================================

              GENERAL SETTINGS TAB

          ================================================= */}

          {activeTab ===

            "general" && (

            <>

              <SectionTitle

                title="Panchayath & Billing Settings"

                description="Configure general Panchayath information, billing options and payment QR codes."

              />

              {!activePanId ? (

                <Alert variant="warning" className="gs-pan-warning">

                  Please select a

                  Panchayath before

                  managing settings.

                </Alert>

              ) : loading ? (

                <div className="gs-loading">

                  <Spinner

                    animation="border"

                  />

                  <div>

                    Loading general

                    settings...

                  </div>

                </div>

              ) : (

                <Form

                  onSubmit={

                    onSubmitGeneral

                  }

                >

                  {/* -----------------------------------------

                      BASIC DETAILS

                  ----------------------------------------- */}

                  <Card className="gs-section-card">

                    <Card.Body>

                      <SectionTitle

                        title="Basic Details"

                        description="Basic Panchayath contact information."

                      />

                      <Row className="g-3">

                        <Col

                          xs={12}

                          md={4}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              Panchayath Name

                            </Form.Label>

                            <Form.Control

                              className="gs-control"

                              value={

                                form.title

                              }

                              placeholder="Enter Panchayath Name"

                              autoComplete="off"

                              disabled={

                                !canUpdate

                              }

                              onChange={(

                                event

                              ) =>

                                setForm(

                                  (

                                    previous

                                  ) => ({

                                    ...previous,

                                    title:

                                      event

                                        .target

                                        .value,

                                  })

                                )

                              }

                            />

                          </Form.Group>

                        </Col>

                        <Col

                          xs={12}

                          md={4}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              SLRM Panchayath Name

                            </Form.Label>

                            <Form.Control

                              className="gs-control"

                              value={form.slrm_pan_name}

                              placeholder="Enter SLRM Panchayath Name"

                              autoComplete="off"

                              disabled={!canUpdate}

                              onChange={(event) =>

                                setForm((previous) => ({

                                  ...previous,

                                  slrm_pan_name: event.target.value,

                                }))

                              }

                            />

                          </Form.Group>

                        </Col>

                        <Col

                          xs={12}

                          md={4}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              Mobile

                            </Form.Label>

                            <Form.Control

                              className="gs-control"

                              value={

                                form.phone

                              }

                              placeholder="Enter Mobile Number"

                              autoComplete="off"

                              disabled={

                                !canUpdate

                              }

                              onChange={(

                                event

                              ) =>

                                setForm(

                                  (

                                    previous

                                  ) => ({

                                    ...previous,

                                    phone:

                                      event

                                        .target

                                        .value,

                                  })

                                )

                              }

                            />

                          </Form.Group>

                        </Col>

                        <Col

                          xs={12}

                          md={4}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              Address

                            </Form.Label>

                            <Form.Control

                              className="gs-control"

                              value={

                                form.address

                              }

                              placeholder="Enter Address"

                              autoComplete="off"

                              disabled={

                                !canUpdate

                              }

                              onChange={(

                                event

                              ) =>

                                setForm(

                                  (

                                    previous

                                  ) => ({

                                    ...previous,

                                    address:

                                      event

                                        .target

                                        .value,

                                  })

                                )

                              }

                            />

                          </Form.Group>

                        </Col>

                      </Row>

                    </Card.Body>

                  </Card>

                  {/* -----------------------------------------

                      WATER + SLRM SETTINGS

                  ----------------------------------------- */}

                  <Card className="gs-section-card">

                    <Card.Body>

                      <SectionTitle

                        title="Billing Rules"

                        description="Configure Water and SLRM combined billing and receipt behaviour."

                      />

                      <Row className="g-3">

                        <Col

                          xs={12}

                          md={6}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              SLRM + Water Together

                            </Form.Label>

                            <Form.Select

                              className="gs-control"

                              value={

                                form.slrm_water_together

                              }

                              disabled={

                                !canUpdate

                              }

                              onChange={(

                                event

                              ) =>

                                handleSlrmTogetherChange(

                                  event

                                    .target

                                    .value

                                )

                              }

                            >

                              <option value="no">

                                No

                              </option>

                              <option value="yes">

                                Yes

                              </option>

                            </Form.Select>

                          </Form.Group>

                        </Col>

                        <Col

                          xs={12}

                          md={6}

                        >

                          <Form.Group>

                            <Form.Label className="gs-label">

                              Receipt Type

                            </Form.Label>

                            <Form.Select

                              className="gs-control"

                              value={

                                form.receipt_type

                              }

                              disabled={

                                !canUpdate ||

                                form.slrm_water_together !==

                                  "yes"

                              }

                              onChange={(

                                event

                              ) =>

                                setForm(

                                  (

                                    previous

                                  ) => ({

                                    ...previous,

                                    receipt_type:

                                      normalizeReceiptType(

                                        event

                                          .target

                                          .value

                                      ),

                                  })

                                )

                              }

                            >

                              <option value="separate">

                                Separate

                              </option>

                              <option value="together">

                                Together

                              </option>

                            </Form.Select>

                            <div className="gs-help">

                              {form.slrm_water_together ===

                              "no"

                                ? "Receipt Type will remain Separate when Water + SLRM Together is No."

                                : form.receipt_type ===

                                    "together"

                                  ? "One receipt collects SLRM first and the remaining amount goes to Water."

                                  : "Water and SLRM receipts are collected separately."}

                            </div>

                          </Form.Group>

                        </Col>

                      </Row>

                    </Card.Body>

                  </Card>

                  {/* -----------------------------------------

                      QR

                  ----------------------------------------- */}

                  <Card className="gs-section-card">

                    <Card.Body>

                      <SectionTitle

                        title="Payment QR"

                        description="Configure Water and SLRM payment QR images."

                      />

                      <Row className="g-4">

                        <Col

                          xs={12}

                          lg={6}

                        >

                          <QrUpload

                            label="Water Payment QR"

                            src={

                              waterQrSrc

                            }

                            disabled={

                              !canUpdate

                            }

                            onPick={(

                              file

                            ) =>

                              onPickQr(

                                "water_payment_qr_file",

                                "water_payment_qr",

                                "remove_water_payment_qr",

                                file

                              )

                            }

                            onRemove={() =>

                              clearQr(

                                "water_payment_qr_file",

                                "water_payment_qr",

                                "remove_water_payment_qr"

                              )

                            }

                          />

                        </Col>

                        <Col

                          xs={12}

                          lg={6}

                        >

                          <QrUpload

                            label="SLRM Payment QR"

                            src={

                              slrmQrSrc

                            }

                            disabled={

                              !canUpdate

                            }

                            onPick={(

                              file

                            ) =>

                              onPickQr(

                                "slrm_payment_qr_file",

                                "slrm_payment_qr",

                                "remove_slrm_payment_qr",

                                file

                              )

                            }

                            onRemove={() =>

                              clearQr(

                                "slrm_payment_qr_file",

                                "slrm_payment_qr",

                                "remove_slrm_payment_qr"

                              )

                            }

                          />

                        </Col>

                      </Row>

                    </Card.Body>

                  </Card>
                  {/* -----------------------------------------
                      WATER READING SETTINGS
                  ----------------------------------------- */}
                  <Card className="gs-section-card">
                    <Card.Body>
                      <SectionTitle
                        title="Water Reading Settings"
                        description="Configure Water reading charge, measuring unit and unit conversion."
                      />
                      <Row className="g-3">
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="gs-label">Water Reading Charge</Form.Label>
                            <Form.Control
                              className="gs-control"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.reading_charge}
                              disabled={!canUpdate}
                              onChange={(event) =>
                                setForm((previous) => ({
                                  ...previous,
                                  reading_charge: event.target.value,
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="gs-label">Measuring Unit</Form.Label>
                            <Form.Control
                              className="gs-control"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.reading_measure}
                              disabled={!canUpdate}
                              onChange={(event) =>
                                setForm((previous) => ({
                                  ...previous,
                                  reading_measure: event.target.value,
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="gs-label">Unit Conversion</Form.Label>
                            <Form.Control
                              className="gs-control"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.unit_conv}
                              disabled={!canUpdate}
                              onChange={(event) =>
                                setForm((previous) => ({
                                  ...previous,
                                  unit_conv: event.target.value,
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* -----------------------------------------
                      SLRM READING SETTINGS
                  ----------------------------------------- */}
                  <Card className="gs-section-card">
                    <Card.Body>
                      <SectionTitle
                        title="SLRM Reading Settings"
                        description="Configure the fixed SLRM reading charge."
                      />
                      <Row className="g-3">
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="gs-label">SLRM Reading Charge</Form.Label>
                            <Form.Control
                              className="gs-control"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.slrm_reading_charge}
                              disabled={!canUpdate}
                              onChange={(event) =>
                                setForm((previous) => ({
                                  ...previous,
                                  slrm_reading_charge: event.target.value,
                                }))
                              }
                            />
                            <div className="gs-help">Applied when Water + SLRM Together is No.</div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* -----------------------------------------

                      SAVE GENERAL

                  ----------------------------------------- */}

                  <div className="gs-save-bar">

                    <Button

                      type="submit"

                      className="gs-primary-btn"

                      disabled={

                        !canUpdate ||

                        saving

                      }

                    >

                      {saving ? (

                        <>

                          <Spinner

                            size="sm"

                            animation="border"

                            className="me-2"

                          />

                          Saving...

                        </>

                      ) : (

                        "Save General Settings"

                      )}

                    </Button>

                  </div>

                </Form>

              )}

            </>

          )}

          {/* =================================================

              APPROVAL SETTINGS TAB

          ================================================= */}

          {activeTab ===

            "approval" && (

            <>

              <SectionTitle

                title="Approval Settings"

                description="Configure whether approval is required and select the responsible approval user."

              />

              {!activePanId ? (

                <Alert variant="warning" className="gs-pan-warning">

                  Please select a

                  Panchayath to

                  configure approval

                  settings.

                </Alert>

              ) : (

                <>

                  {usersError ? (

                    <Alert variant="danger">

                      {usersError}

                    </Alert>

                  ) : null}

                  {approvalLoading ? (

                    <div className="gs-loading">

                      <Spinner

                        animation="border"

                      />

                      <div>

                        Loading approval

                        settings...

                      </div>

                    </div>

                  ) : (

                    <Card className="gs-approval-card">

                      <Card.Body>

                        {/* HEADER */}

                        <div className="gs-approval-header d-none d-md-block">

                          <Row className="g-3">

                          <Col md={4}>

                            <div className="gs-approval-heading">

                              Approval Type

                            </div>

                          </Col>

                          <Col md={3}>

                            <div className="gs-approval-heading">

                              Approval Required

                            </div>

                          </Col>

                          <Col md={5}>

                            <div className="gs-approval-heading">

                              Approval User

                            </div>

                          </Col>

                          </Row>

                        </div>

                        {/* DISCONNECTION */}

                        <ApprovalRow

                          title="Disconnection Approval"

                          description="Required before disconnecting Water / SLRM services for a house."

                          approvalValue={

                            approvalForm

                              .disconnection_approval_required

                          }

                          userValue={

                            approvalForm

                              .disconnection_approver_user_id

                          }

                          users={

                            approvalUsers

                          }

                          usersLoading={

                            usersLoading

                          }

                          disabled={

                            !canUpdate

                          }

                          onApprovalChange={(

                            value

                          ) =>

                            handleApprovalChange(

                              "disconnection_approval_required",

                              "disconnection_approver_user_id",

                              value

                            )

                          }

                          onUserChange={(

                            value

                          ) =>

                            setApprovalForm(

                              (

                                previous

                              ) => ({

                                ...previous,

                                disconnection_approver_user_id:

                                  value,

                              })

                            )

                          }

                        />

                        {/* RECONNECTION */}

                        <ApprovalRow

                          title="Reconnection Approval"

                          description="Required before reconnecting a previously disconnected house."

                          approvalValue={

                            approvalForm

                              .reconnection_approval_required

                          }

                          userValue={

                            approvalForm

                              .reconnection_approver_user_id

                          }

                          users={

                            approvalUsers

                          }

                          usersLoading={

                            usersLoading

                          }

                          disabled={

                            !canUpdate

                          }

                          onApprovalChange={(

                            value

                          ) =>

                            handleApprovalChange(

                              "reconnection_approval_required",

                              "reconnection_approver_user_id",

                              value

                            )

                          }

                          onUserChange={(

                            value

                          ) =>

                            setApprovalForm(

                              (

                                previous

                              ) => ({

                                ...previous,

                                reconnection_approver_user_id:

                                  value,

                              })

                            )

                          }

                        />

                        {/* DISCOUNT */}

                        <ApprovalRow

                          title="Discount Approval"

                          description="Required before a house billing discount is approved."

                          approvalValue={

                            approvalForm

                              .discount_approval_required

                          }

                          userValue={

                            approvalForm

                              .discount_approver_user_id

                          }

                          users={

                            approvalUsers

                          }

                          usersLoading={

                            usersLoading

                          }

                          disabled={

                            !canUpdate

                          }

                          onApprovalChange={(

                            value

                          ) =>

                            handleApprovalChange(

                              "discount_approval_required",

                              "discount_approver_user_id",

                              value

                            )

                          }

                          onUserChange={(

                            value

                          ) =>

                            setApprovalForm(

                              (

                                previous

                              ) => ({

                                ...previous,

                                discount_approver_user_id:

                                  value,

                              })

                            )

                          }

                        />

                        {/* SAVE APPROVAL */}

                        <div className="gs-save-bar">

                          <Button

                            type="button"

                            className="gs-primary-btn"

                            disabled={

                              !canUpdate ||

                              approvalSaving ||

                              approvalLoading ||

                              !activePanId

                            }

                            onClick={

                              saveApprovalSettings

                            }

                          >

                            {approvalSaving ? (

                              <>

                                <Spinner

                                  animation="border"

                                  size="sm"

                                  className="me-2"

                                />

                                Saving...

                              </>

                            ) : (

                              "Save Approval Settings"

                            )}

                          </Button>

                        </div>

                      </Card.Body>

                    </Card>

                  )}

                </>

              )}

            </>

          )}

        </Card.Body>

      </Card>

    </div>

  );

}
